import { useMemo, useState } from 'react';
import type { VigenciaResponse } from '@triade/contracts';
import { X } from 'lucide-react';
import { useNovaVigencia } from '@/api/tabelas-preco';
import { useIcmsLegal, useTributosReforma } from '@/api/tabelas-preco-tributos';
import { useEmpresaPerfil } from '@/api/empresa';
import { useClientes } from '@/api/clientes';
import { AbaTributos } from './aba-tributos';
import {
  BASE_DESPESA_ROTULO,
  DESPESA_VARIAVEL_VAZIA,
  TIPO_DESPESA_ROTULO,
  TIPO_DESPESA_VARIAVEL,
  mensagemErroTabela,
  novaVigenciaVazia,
  paraNovaVigencia,
  perfilDeResposta,
  type BaseDespesa,
  type DespesaEntrada,
  type DespesaTipo,
  type NovaVigenciaEntrada,
} from './tabela-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const BOTAO = 'h-[34px] rounded-[10px] border border-input bg-card px-3 text-small font-semibold text-ink-body hover:bg-line-soft disabled:opacity-50';
const BOTAO_PRIMARIO = 'h-[34px] rounded-[10px] bg-primary px-4 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50';

export interface NovaVigenciaEstruturadaProps {
  tabelaId: string;
  podeEditar: boolean;
  /** Vigencia em aberto: os parametros tributarios dela pre-preenchem a nova (valores da API, nada calculado). */
  vigenciaAberta: VigenciaResponse | undefined;
  onAviso: (texto: string) => void;
}

/** Nova vigencia no modo ESTRUTURADO (padrao): lucro, despesas variaveis e a aba Tributos. */
export function NovaVigenciaEstruturada({ tabelaId, podeEditar, vigenciaAberta, onAviso }: NovaVigenciaEstruturadaProps) {
  const nova = useNovaVigencia();
  const empresa = useEmpresaPerfil();
  const legal = useIcmsLegal();
  const reforma = useTributosReforma({}, podeEditar);
  const clientes = useClientes({ limit: 200 });
  const [v, setV] = useState<NovaVigenciaEntrada>(() =>
    novaVigenciaVazia(vigenciaAberta?.perfilTributario ? perfilDeResposta(vigenciaAberta.perfilTributario) : undefined),
  );
  const [erro, setErro] = useState<string | null>(null);

  /** UFs com cliente cadastrado (filtro da grade). Sem lista, a grade mostra as 27. */
  const ufsComClientes = useMemo(
    () => (clientes.data ? new Set(clientes.data.map((c) => c.uf).filter((uf): uf is string => !!uf)) : undefined),
    [clientes.data],
  );

  if (!podeEditar) return <p className="text-small text-ink-body" role="status">Seu acesso não permite registrar vigências.</p>;

  const atualizarDespesa = (i: number, parcial: Partial<DespesaEntrada>) =>
    setV({ ...v, despesas: v.despesas.map((d, k) => (k === i ? { ...d, ...parcial } : d)) });

  async function gravar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    const r = paraNovaVigencia(v);
    if (!r.ok) return setErro(r.mensagem);
    try {
      await nova.mutateAsync({ tabelaId, ...r.dados });
      onAviso('Nova vigência registrada. A anterior foi encerrada na data de início.');
      setV(novaVigenciaVazia());
    } catch (err) {
      setErro(mensagemErroTabela(err));
    }
  }

  return (
    <form onSubmit={gravar} aria-label="Nova vigência" className="flex flex-col gap-6">
      <p className="text-small text-ink-secondary">Valores nunca são editados: mudar gera uma nova vigência, a partir da data de início (nada retroativo).</p>
      <div className="grid gap-4 sm:grid-cols-4">
        <div>
          <label htmlFor="vig-inicio" className={ROTULO}>Início</label>
          <input id="vig-inicio" type="date" className={CAMPO} min={novaVigenciaVazia().validFrom} value={v.validFrom} onChange={(e) => setV({ ...v, validFrom: e.target.value })} />
        </div>
        <div>
          <label htmlFor="vig-desejado" className={ROTULO}>Lucro desejado (%)</label>
          <input id="vig-desejado" inputMode="decimal" className={CAMPO} placeholder="ex.: 20" value={v.lucroDesejado} onChange={(e) => setV({ ...v, lucroDesejado: e.target.value })} />
          <p className="mt-1 text-caption text-ink-muted">Vazio = usa o padrão da empresa</p>
        </div>
        <div>
          <label htmlFor="vig-minimo" className={ROTULO}>Lucro mínimo (%)</label>
          <input id="vig-minimo" inputMode="decimal" className={CAMPO} placeholder="ex.: 10" value={v.lucroMinimo} onChange={(e) => setV({ ...v, lucroMinimo: e.target.value })} />
          <p className="mt-1 text-caption text-ink-muted">Vazio = usa o padrão da empresa</p>
        </div>
        <div>
          <label htmlFor="vig-motivo" className={ROTULO}>Motivo</label>
          <input id="vig-motivo" className={CAMPO} maxLength={300} value={v.motivo} onChange={(e) => setV({ ...v, motivo: e.target.value })} />
        </div>
      </div>

      <section aria-label="Tributos da vigência" className="flex flex-col gap-3 rounded-xl border border-border p-4">
        <h3 className="text-title font-semibold">Tributos</h3>
        <AbaTributos
          perfil={v.perfil}
          onChange={(perfil) => setV({ ...v, perfil })}
          podeEditar={podeEditar}
          ufEmpresa={empresa.data?.uf ?? null}
          regimeEmpresa={empresa.data?.regimeTributario ?? null}
          legal={legal.data}
          legalErro={legal.isError}
          reforma={reforma.data}
          reformaErro={reforma.isError}
          ufsComClientes={ufsComClientes}
        />
      </section>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-caption font-medium text-ink-secondary">Despesas variáveis</span>
          <button type="button" className={BOTAO} onClick={() => setV({ ...v, despesas: [...v.despesas, { ...DESPESA_VARIAVEL_VAZIA }] })}>
            Adicionar despesa
          </button>
        </div>
        <p className="mb-2 text-caption text-ink-muted">Só despesas não tributárias (comissão etc.). Tributo vai na aba Tributos.</p>
        {v.despesas.length === 0 ? (
          <p className="text-small text-ink-muted">Sem despesas nesta vigência.</p>
        ) : (
          <table className="w-full text-small" aria-label="Grade de despesas variáveis">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="py-1 font-medium">Tipo</th>
                <th scope="col" className="py-1 font-medium">Descrição</th>
                <th scope="col" className="py-1 font-medium">Base</th>
                <th scope="col" className="py-1 font-medium">%</th>
                <th scope="col" className="py-1"><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              {v.despesas.map((d, i) => (
                <tr key={i} className="border-t border-line-soft">
                  <td className="w-40 py-1.5 pr-2">
                    <select aria-label={`Tipo da despesa ${i + 1}`} className={CAMPO} value={d.tipo} onChange={(e) => atualizarDespesa(i, { tipo: e.target.value as DespesaTipo })}>
                      {TIPO_DESPESA_VARIAVEL.map((k) => (
                        <option key={k} value={k}>{TIPO_DESPESA_ROTULO[k]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input aria-label={`Descrição da despesa ${i + 1}`} className={CAMPO} maxLength={200} value={d.descricao} onChange={(e) => atualizarDespesa(i, { descricao: e.target.value })} />
                  </td>
                  <td className="w-44 py-1.5 pr-2">
                    <select aria-label={`Base da despesa ${i + 1}`} className={CAMPO} value={d.base} onChange={(e) => atualizarDespesa(i, { base: e.target.value as BaseDespesa })}>
                      {(Object.keys(BASE_DESPESA_ROTULO) as BaseDespesa[]).map((k) => (
                        <option key={k} value={k}>{BASE_DESPESA_ROTULO[k]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="w-32 py-1.5 pr-2">
                    <input aria-label={`Percentual da despesa ${i + 1}`} inputMode="decimal" className={CAMPO} placeholder="ex.: 18" value={d.percentual} onChange={(e) => atualizarDespesa(i, { percentual: e.target.value })} />
                  </td>
                  <td className="w-10 py-1.5">
                    <button type="button" aria-label={`Remover despesa ${i + 1}`} className="rounded-md p-1 text-ink-muted hover:bg-line-soft" onClick={() => setV({ ...v, despesas: v.despesas.filter((_, k) => k !== i) })}>
                      <X aria-hidden="true" className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-2 text-caption text-ink-muted">O total é calculado ao salvar.</p>
      </div>

      {erro && <p role="alert" className="text-small text-danger-ink">{erro}</p>}
      <div>
        <button type="submit" className={BOTAO_PRIMARIO} disabled={nova.isPending}>
          Registrar nova vigência
        </button>
      </div>
    </form>
  );
}
